import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { clubMembersService } from '@/services/clubMembers';

interface SendClubNoticeModalProps {
  clubId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMemberIds: string[];
  onSuccess?: () => void;
}

export function SendClubNoticeModal({
  clubId,
  open,
  onOpenChange,
  selectedMemberIds,
  onSuccess,
}: SendClubNoticeModalProps) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [inApp, setInApp] = useState(true);
  const [email, setEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubject('');
      setMessage('');
      setInApp(true);
      setEmail(false);
    }
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (!submitting) onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    if (!inApp && !email) {
      toast.error('Select at least one delivery channel');
      return;
    }
    if (selectedMemberIds.length === 0) {
      toast.error('Select at least one member');
      return;
    }

    setSubmitting(true);
    try {
      const res = await clubMembersService.sendClubNotice(clubId, {
        title: subject.trim(),
        body: message.trim(),
        memberIds: selectedMemberIds,
        channels: { inApp, email },
      });
      if (res.success) {
        toast.success('Notice sent to members');
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast.error(res.error || 'Failed to send notice');
      }
    } catch {
      toast.error('Failed to send notice');
    } finally {
      setSubmitting(false);
    }
  };

  const totalChars = message.length + subject.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-950">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Message members</h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {selectedMemberIds.length} member{selectedMemberIds.length !== 1 ? 's' : ''} selected.
          Members with a linked account see in-app notices in their portal; email goes to the
          address on the member record when enabled.
        </p>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              required
              maxLength={200}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              required
              maxLength={5000}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={inApp}
                onChange={(e) => setInApp(e.target.checked)}
                className="rounded border-gray-300"
              />
              In-app notification
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={email}
                onChange={(e) => setEmail(e.target.checked)}
                className="rounded border-gray-300"
              />
              Email
            </label>
            {email && (
              <span className="text-xs text-muted-foreground pl-6">
                Sent to selected members with an email on file. Subject and message total about{' '}
                {totalChars} characters.
              </span>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || (!inApp && !email)}>
              {submitting ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
